import { useEffect } from 'react';

// Component to enhance accessibility across the application
const AccessibilityEnhancements = () => {
  useEffect(() => {
    // Add skip link functionality
    const skipLink = document.querySelector('a[href="#main-content"]');
    const mainContent = document.getElementById('main-content');
    
    if (skipLink && mainContent) {
      skipLink.addEventListener('click', (e) => {
        e.preventDefault();
        mainContent.focus();
        mainContent.scrollIntoView({ behavior: 'smooth' });
      });
    }

    // Enhanced focus management and accessibility styles
    const style = document.createElement('style');
    style.textContent = `
      /* Enhanced focus indicators */
      *:focus-visible {
        outline: 2px solid hsl(var(--primary)) !important;
        outline-offset: 2px !important;
        box-shadow: 0 0 0 4px hsl(var(--primary) / 0.2) !important;
      }
      
      /* Enhanced link contrast and underlines */
      a:not(.no-underline):not(.btn):not(.button):not([class*="button"]) {
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-underline-offset: 2px;
        color: hsl(var(--primary));
        font-weight: 500;
      }
      
      a:hover:not(.no-underline):not(.btn):not(.button):not([class*="button"]) {
        text-decoration-thickness: 2px;
        color: hsl(var(--primary) / 0.8);
      }
      
      /* Button accessibility */
      button[aria-label]:empty::after {
        content: attr(aria-label);
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      }
      
      /* Link accessibility */
      a[aria-label]:empty::after {
        content: attr(aria-label);
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      }
      
      /* Screen reader only text */
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
      
      .sr-only.focus:focus {
        position: static;
        width: auto;
        height: auto;
        padding: inherit;
        margin: inherit;
        overflow: visible;
        clip: auto;
        white-space: normal;
      }
      
      /* High contrast mode support */
      @media (prefers-contrast: high) {
        * {
          border-color: ButtonText !important;
        }
        
        button, [role="button"] {
          border: 2px solid ButtonText !important;
        }
      }
      
      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Add aria-label to buttons without accessible names
    const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');
    buttons.forEach((button) => {
      const text = button.textContent?.trim();
      if (!text || text.length === 0) {
        button.setAttribute('aria-label', 'Unlabeled button');
      }
    });

    // Add aria-label to links without accessible names
    const links = document.querySelectorAll('a:not([aria-label]):not([aria-labelledby])');
    links.forEach((link) => {
      const text = link.textContent?.trim();
      if (!text || text.length === 0) {
        link.setAttribute('aria-label', 'Unlabeled link');
      }
    });

    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  return null;
};

export default AccessibilityEnhancements;